// libs
import cx from "classnames";
import React, { ReactElement, useCallback, useState, useRef, RefObject, useEffect, useMemo } from "react";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { IFullWidthButtonProps } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton/types";

// components
import { BottomSheet, BottomSheetChildrenProps } from "../../components/bottom-sheet";
import { replaceText } from "../../helpers/HtmlFormatter";
import { ACCESSIBILITY_ROLES } from "@swiggy-private/core-types";

// widgets
import PaymentMethodIcon from "../../widgets/payment-method-icon/PaymentMethodIcon";

// utils
import { useFocusTrap } from "../../hooks/UseFocusTrap";
import { getCloudinaryUrl } from "../../helpers/Image";
import { isEmpty } from "@swiggy-private/js-utils";

// types
import { PaymentAlertProps } from "./types";

// constants
import {
    PAYMENT_ALERT_HEADER_TITLE_ID,
    PAYMENT_ALERT_CONTENT_ID,
    PAYMENT_ALERT_TEST_IDS,
} from "./constants";

//hooks
import { useFocusDialog } from "../../hooks/useFocusDialog";
import { getActiveElement } from "../../helpers/ActiveElement";

import styles from "./PaymentAlert.scss";

const ICON_PAYMENT_ALERT_V4 = "PaymentLogos/paymentIcons/error";

type AlertContentProps = Omit<PaymentAlertProps, "hideOnOverlayClick" | "onClose"> & {
    setAction: (fn: VoidFunction) => void;
};

interface IPaymentAlertButtonProps extends IFullWidthButtonProps {
    "data-testid": string;
}

const AlertContentComponent = React.memo(function AlertContent(
    props: AlertContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    const {
        setAction,
        primaryAction,
        secondaryAction,
        close,
        hideIcon,
        renderPrimaryCTA: renderPrimaryCTAProp,
    } = props;

    const iconUrl = getCloudinaryUrl(props.icon || ICON_PAYMENT_ALERT_V4, {
        trim: true,
    });
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const handlePrimaryActionClick = useCallback((): void => {
        setAction(primaryAction.onClick);
        close();
    }, [close, primaryAction.onClick, setAction]);

    const handleSecondaryActionClick = useCallback((): void => {
        if (!secondaryAction) {
            close();
            return;
        }

        // setting after close action to secondary action handler
        setAction(secondaryAction.onClick);
        close();
    }, [close, secondaryAction, setAction]);

    const actionClass = cx({
        [styles.paymentAlertActionSingle]: !props.secondaryAction,
    });

    const renderPrimaryCTA = useMemo((): ReactElement => {
        const buttonProps: IPaymentAlertButtonProps = {
            onClick: handlePrimaryActionClick,
            variant: "primary",
            className: actionClass,
            subLabel: props.primaryAction.subText,
            "data-testid": PAYMENT_ALERT_TEST_IDS.PRIMARY_ACTION_BUTTON,
        };

        if (renderPrimaryCTAProp && typeof renderPrimaryCTAProp === "function") {
            return renderPrimaryCTAProp(buttonProps);
        }

        return <FullWidthButton {...buttonProps}>{props.primaryAction.text}</FullWidthButton>;
    }, [actionClass, handlePrimaryActionClick, props.primaryAction, renderPrimaryCTAProp]);

    return (
        <div
            role={ACCESSIBILITY_ROLES.ALERTDIALOG}
            aria-labelledby={PAYMENT_ALERT_HEADER_TITLE_ID}
            aria-describedby={PAYMENT_ALERT_CONTENT_ID}
        >
            <div className={styles.paymentAlertHeader} data-testid={PAYMENT_ALERT_TEST_IDS.HEADER}>
                <div className={styles.paymentAlertHeaderInfo}>
                    {props.info && (
                        <div className={styles.paymentAlertContentRow} id={PAYMENT_ALERT_CONTENT_ID}>
                            <Text
                                variant="V2.body3.neutral.regular"
                                color="text_Med_Emphasis"
                                className={styles.paymentAlertInfo}
                                data-testid={PAYMENT_ALERT_TEST_IDS.ALERT_INFO}
                            >
                                {props.info} {!isEmpty(props.subInfo) && props.subInfo}
                            </Text>
                        </div>
                    )}
                    <div className={styles.paymentAlertHeaderContainer}>
                        <div>
                            {!hideIcon && (
                                <PaymentMethodIcon
                                    src={iconUrl}
                                    data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_ICON}
                                    parentClassName={styles.paymentAlertIcon}
                                />
                            )}
                        </div>
                        <div className={styles.paymentAlertHeaderContent}>
                            <Text
                                variant="V2.heading2.neutral.bold"
                                color="text_Highest_Emphasis"
                                id={PAYMENT_ALERT_HEADER_TITLE_ID}
                                className={styles.paymentAlertHeaderTitle}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_TITLE}
                                ref={headerDivRef as RefObject<HTMLDivElement>}
                                tabIndex={0}
                                role={ACCESSIBILITY_ROLES.HEADING}
                                aria-level={3}
                            >
                                {props.title || ""}
                            </Text>
                            <Text
                                variant="V2.body2.neutral.regular"
                                color="text_Med_Emphasis"
                                className={styles.paymentAlertHeaderMessage}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_MESSAGE}
                            >
                                {replaceText(props.message || "")}
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.paymentAlertContent} data-testid={PAYMENT_ALERT_TEST_IDS.CONTENT}>
                <div className={styles.paymentAlertContentRow}>{renderPrimaryCTA}</div>

                {props.secondaryAction && (
                    <div className={styles.paymentAlertContentRow}>
                        <TextButton
                            onClick={handleSecondaryActionClick}
                            color="primary"
                            buttonSize="medium"
                            className={styles.paymentAlertActionSecondary}
                            data-testid={PAYMENT_ALERT_TEST_IDS.SECONDARY_ACTION_BUTTON}
                        >
                            <div>{props.secondaryAction.text}</div>
                            {props.secondaryAction.subText && (
                                <div className={styles.paymentAlertActionSub}>{props.secondaryAction.subText}</div>
                            )}
                        </TextButton>
                    </div>
                )}
            </div>
        </div>
    );
});

function PaymentAlert(props: PaymentAlertProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, hideCloseIcon = false, closeButtonDataTestId, ..._props } = props;
    const [actionOnClose, setActionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    useFocusTrap();
    return (
        <BottomSheet
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={_onClose}
            hideCloseIcon={hideCloseIcon}
            closeButtonDataTestId={closeButtonDataTestId}
        >
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <AlertContentComponent {...bProps} {..._props} setAction={setActionOnClose} />
            )}
        </BottomSheet>
    );
}

export default React.memo(PaymentAlert);
